import { createEconomyTemplate, getAvailableAttributes } from '@/api/economyTemplates';
import { useAuth } from '@/hooks/useAuth';
import { EconomyTemplateCreate, EconomyTemplateType } from '@/types/economyTemplate';
import { UserRole } from '@/types';
import {
  ECONOMY_TEMPLATE_VISIBILITY,
  getEconomyTemplateTypeLabel,
} from '@/utils/economyTemplateLabels';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import {
  EconomyParamAttribute,
  EconomyTemplateParamsForm,
  formatFormRateForStorage,
  RATE_FIELD_NAMES,
} from './EconomyTemplateParamsForm';

interface CreateTemplateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CURRENCIES = ['USD', 'EUR', 'GBP'];

const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({ open, onClose, onSuccess }) => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === UserRole.ADMIN || user?.role === 'admin';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableAttributes, setAvailableAttributes] = useState<EconomyParamAttribute[]>([]);
  const [attributeValues, setAttributeValues] = useState<Record<string, string>>({});

  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');
  const [templateType, setTemplateType] = useState<EconomyTemplateType>('field');
  const [currency, setCurrency] = useState('USD');
  const [unitId, setUnitId] = useState(1);
  const [visibilityStatusId, setVisibilityStatusId] = useState(ECONOMY_TEMPLATE_VISIBILITY.PRIVATE);

  useEffect(() => {
    if (open) {
      fetchAvailableAttributes(templateType);
    }
  }, [open, templateType]);

  const fetchAvailableAttributes = async (scope: EconomyTemplateType) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAvailableAttributes(scope);
      setAvailableAttributes(data);
      const initialValues: Record<string, string> = {};
      data.forEach((attr) => {
        initialValues[attr.id] = '';
      });
      setAttributeValues(initialValues);
    } catch {
      setError('Failed to load available attributes');
    } finally {
      setLoading(false);
    }
  };

  const handleAttributeChange = (attributeId: string, value: string) => {
    setAttributeValues((prev) => ({ ...prev, [attributeId]: value }));
  };

  const resetForm = () => {
    setTemplateName('');
    setDescription('');
    setTemplateType('field');
    setCurrency('USD');
    setUnitId(1);
    setVisibilityStatusId(ECONOMY_TEMPLATE_VISIBILITY.PRIVATE);
    setAttributeValues({});
    setError(null);
  };

  const handleSave = async () => {
    if (!templateName.trim()) {
      setError('Template name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const attrById = new Map(availableAttributes.map((a) => [a.id, a]));
      const attributes = Object.entries(attributeValues)
        .filter(([, value]) => value.trim() !== '')
        .map(([attributeId, value]) => {
          const meta = attrById.get(attributeId);
          const stored = meta && RATE_FIELD_NAMES.has(meta.name)
            ? formatFormRateForStorage(meta.name, value)
            : value.trim();
          return { economy_attribute_id: attributeId, value: stored };
        });

      const templateData: EconomyTemplateCreate = {
        name: templateName.trim(),
        description: description.trim() || undefined,
        visibility_status_id: visibilityStatusId,
        template_type: templateType,
        currency,
        unit_id: unitId,
        attributes,
      };

      await createEconomyTemplate(templateData);
      onSuccess();
      resetForm();
      onClose();
    } catch {
      setError('Failed to create template');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create New Economy Template</DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" mb={2}>
            <CircularProgress />
          </Box>
        )}

        {!loading && (
          <Box>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Template Name"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Template type</InputLabel>
                  <Select
                    label="Template type"
                    value={templateType}
                    onChange={(e) => setTemplateType(e.target.value as EconomyTemplateType)}
                  >
                    <MenuItem value="field">Field economics</MenuItem>
                    <MenuItem value="layer">Layer economics</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Visibility</InputLabel>
                  <Select
                    label="Visibility"
                    value={visibilityStatusId}
                    onChange={(e) => setVisibilityStatusId(e.target.value)}
                  >
                    <MenuItem value={ECONOMY_TEMPLATE_VISIBILITY.PRIVATE}>Private</MenuItem>
                    <MenuItem value={ECONOMY_TEMPLATE_VISIBILITY.COMPANY_PUBLIC}>
                      Company Public
                    </MenuItem>
                    {isSuperAdmin && (
                      <MenuItem value={ECONOMY_TEMPLATE_VISIBILITY.PUBLIC}>Public</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select label="Currency" value={currency} onChange={(e) => setCurrency(e.target.value)}>
                    {CURRENCIES.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Production units</InputLabel>
                  <Select
                    label="Production units"
                    value={unitId}
                    onChange={(e) => setUnitId(Number(e.target.value))}
                  >
                    <MenuItem value={1}>Tons</MenuItem>
                    <MenuItem value={2}>Barrels</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  placeholder="Describe when and how to use this template"
                  multiline
                  minRows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Grid>
            </Grid>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {getEconomyTemplateTypeLabel(templateType)} · {currency}
            </Typography>

            {availableAttributes.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Template Parameters
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Only the template name is required to save. Parameters marked with * are needed
                  when running economics; others can be left empty.
                </Typography>
                <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
                  <EconomyTemplateParamsForm
                    attributes={availableAttributes}
                    templateType={templateType}
                    unitId={unitId}
                    values={attributeValues}
                    onChange={handleAttributeChange}
                  />
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || !templateName.trim()}
        >
          {loading ? 'Creating...' : 'Create Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTemplateModal;
